import React, { useState } from 'react';
import { Loader2, Wand2 } from 'lucide-react';
import { translateToArabic } from '../../utils/translationApi';

interface BilingualInputProps {
    label: string;
    englishValue: string;
    arabicValue: string;
    onChangeEnglish: (val: string) => void;
    onChangeArabic: (val: string) => void;
    placeholder?: string;
    required?: boolean;
    className?: string;
    showArabic?: boolean;
}

const BilingualInput: React.FC<BilingualInputProps> = ({
    label,
    englishValue,
    arabicValue,
    onChangeEnglish,
    onChangeArabic,
    placeholder,
    required,
    className = "",
    showArabic = false
}) => {
    const [translating, setTranslating] = useState(false);

    const handleAutoTranslate = async () => {
        if (!englishValue) return;
        setTranslating(true);
        try {
            const token = localStorage.getItem('adminToken') || localStorage.getItem('hrToken');
            const translated = await translateToArabic(englishValue, token);
            if (translated) {
                onChangeArabic(translated);
            }
        } catch (error) {
            console.error('Translation error:', error);
        } finally {
            setTranslating(false);
        }
    };

    return (
        <div className={`space-y-2 ${className}`}>
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">{label}</label>
            <div className={`grid gap-4 ${showArabic ? 'md:grid-cols-2' : 'grid-cols-1'}`}>
                {/* English Input */}
                <div className="relative">
                    <div className="absolute left-3 top-3 text-slate-300 pointer-events-none text-[10px] font-bold uppercase">EN</div>
                    <input
                        type="text"
                        required={required}
                        value={englishValue || ''}
                        onChange={(e) => onChangeEnglish(e.target.value)}
                        placeholder={placeholder || "English text..."}
                        className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-all text-sm font-medium text-slate-900 placeholder:text-slate-400"
                    />
                </div>

                {/* Arabic Input */}
                {showArabic && (
                    <div className="relative">
                        <div className="absolute right-3 top-3 text-slate-300 pointer-events-none text-[10px] font-bold uppercase">AR</div>
                        <input
                            type="text"
                            dir="rtl"
                            value={arabicValue || ''}
                            onChange={(e) => onChangeArabic(e.target.value)}
                            placeholder=""
                            className="w-full pr-10 pl-12 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-all text-sm font-medium font-arabic text-slate-900 placeholder:text-slate-400"
                        />
                        <button
                            type="button"
                            onClick={handleAutoTranslate}
                            disabled={translating || !englishValue}
                            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 text-blue-500 hover:text-blue-700 disabled:text-slate-300 transition-colors"
                            title="Translate"
                        >
                            {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BilingualInput;
